import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Workflow, RefreshCw, Clock, Zap,
  MessageSquare, CheckCircle, Users,
  XCircle, AlertCircle, Send, Calendar, Settings,
  ToggleRight, ToggleLeft, ArrowRight, Trash2
} from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const SKIP_REASONS = {
  no_phone: "No phone number",
  opted_out: "Opted out",
  no_contact: "Couldn't load lead from FUB",
  contact_unavailable: "Lead deleted, opted out, or no phone",
  contact_not_found: "Lead not found",
};

function StatusBadge({ status, reason }) {
  const map = {
    queued: { bg: "#FEF3C7", color: "#92400E", label: "Queued" },
    delivered: { bg: "#D1FAE5", color: "#065F46", label: "Delivered" },
    sent: { bg: "#DBEAFE", color: "#1E40AF", label: "Sent" },
    failed: { bg: "#FEE2E2", color: "#991B1B", label: "Failed" },
    skipped: { bg: "#F3F4F6", color: "#6B7280", label: "Skipped" },
    scheduled: { bg: "#EDE9FE", color: "#5B21B6", label: "Scheduled" },
    mock: { bg: "#FEF3C7", color: "#92400E", label: "Mock" },
  };
  const s = map[status] || map.queued;
  const why = (status === "skipped" || status === "failed") && reason
    ? (String(reason).startsWith("fub_lookup_failed:") ? `FUB lookup failed (${String(reason).split(":")[1]})` : (SKIP_REASONS[reason] || reason))
    : "";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: s.bg, color: s.color }}
      title={why || undefined}
    >
      {s.label}{why ? `: ${why}` : ""}
    </span>
  );
}

export default function FubCampaigns() {
  const [stats, setStats] = useState(null);
  const [sends, setSends] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("overview"); // "overview", "history", or "debug"
  const [debugNotes, setDebugNotes] = useState(null);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [reprocessing, setReprocessing] = useState(null);
  const [resettingWatermark, setResettingWatermark] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    // Load settings separately so a campaign-stats/sends failure doesn't block it
    try {
      const settingsRes = await axios.get(`${API}/api/settings`);
      setSettings(settingsRes.data);
    } catch (e) {
      console.error("Failed to load settings:", e);
    }
    try {
      const [statsRes, sendsRes] = await Promise.all([
        axios.get(`${API}/api/fub/campaign-stats`),
        axios.get(`${API}/api/fub/campaign-sends?limit=100`),
      ]);
      setStats(statsRes.data);
      setSends(sendsRes.data.sends || []);
    } catch (e) {
      console.error("Failed to load FUB campaign data:", e);
    }
    setLoading(false);
  };

  const loadDebug = async () => {
    setLoadingDebug(true);
    try {
      const r = await axios.get(`${API}/api/fub/notes/debug?limit=50`);
      setDebugNotes(r.data);
    } catch (e) { console.error(e); }
    setLoadingDebug(false);
  };

  const reprocessNote = async (noteId) => {
    setReprocessing(noteId);
    try {
      const r = await axios.post(`${API}/api/fub/notes/reprocess`, { note_id: noteId });
      alert(r.data.success ? `Reprocessed note #${noteId} — SMS sent!` : `Error: ${r.data.error}`);
      loadDebug();
      load();
    } catch (e) {
      alert(`Error: ${e.response?.data?.detail || e.message}`);
    }
    setReprocessing(null);
  };

  const resetWatermark = async () => {
    if (!window.confirm("Reset the polling watermark to recent notes? This lets the poller catch up to current notes instantly.")) return;
    setResettingWatermark(true);
    try {
      const r = await axios.post(`${API}/api/fub/notes/reset-watermark`, {});
      alert(`Watermark reset: #${r.data.old_watermark} → #${r.data.new_watermark}. The poller will pick up recent SMS notes on the next cycle (within 2 minutes).`);
      loadDebug();
    } catch (e) {
      alert(`Error: ${e.response?.data?.detail || e.message}`);
    }
    setResettingWatermark(false);
  };

  const deleteCampaignGroup = async (group) => {
    if (!window.confirm(`Delete "${group.name}" and all ${group.sends.length} send(s)? This cannot be undone.`)) return;
    setDeleting(group.id);
    try {
      const sendIds = group.sends.map(s => s.id).filter(Boolean);
      await axios.delete(`${API}/api/fub/campaign-sends`, { data: { send_ids: sendIds } });
      await load();
    } catch (e) {
      alert(`Error: ${e.response?.data?.detail || e.message}`);
    }
    setDeleting(null);
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setSyncing(true);
    await load();
    setSyncing(false);
  };

  const isEnabled = settings?.fub_note_sms_enabled || false;
  const timeHandling = settings?.fub_note_time_handling || "smart_window";
  const isFubConfigured = settings?.fub_configured || false;
  const isSmsConfigured = settings?.sms_configured || false;

  // Group sends by message template (body_raw) — auto-detects campaigns from FUB automations
  const templateGroups = {};
  for (const s of sends) {
    // Normalize: strip [SMS]/time prefixes and merge fields to create a template fingerprint
    const raw = (s.body_raw || s.body || "").replace(/\[SMS\]\s*/gi, "").replace(/\[\d{1,2}:?\d{0,2}\s*(?:am|pm)\]\s*/gi, "").replace(/\[\d+\s*(?:m|min|mins|minutes|h|hr|hrs|hour|hours)\]\s*/gi, "").trim();
    const key = raw.substring(0, 120) || "unknown";
    if (!templateGroups[key]) {
      // Auto-name: take first sentence or first 60 chars of the cleaned template
      const cleanedForName = raw.replace(/%\w+%/g, "___");
      const firstSentence = cleanedForName.split(/[.!?]/)[0].trim();
      const autoName = firstSentence.length > 60 ? firstSentence.substring(0, 57) + "..." : firstSentence;
      templateGroups[key] = {
        id: key,
        name: autoName || "SMS Campaign",
        template: raw.substring(0, 200),
        sends: [],
        contacts: new Set(),
        delivered: 0,
        failed: 0,
        scheduled: 0,
        skipped: 0,
        mock: 0,
        firstSent: null,
        lastSent: null,
      };
    }
    const g = templateGroups[key];
    g.sends.push(s);
    if (s.contact_name && s.contact_name !== "Unknown") g.contacts.add(s.contact_name);
    const st = s.status;
    if (st === "delivered" || st === "sent" || st === "queued") g.delivered++;
    else if (st === "failed") g.failed++;
    else if (st === "scheduled") g.scheduled++;
    else if (st === "skipped") g.skipped++;
    else if (st === "mock") g.mock++;
    const ts = s.sent_at || s.created_at;
    if (ts) {
      if (!g.firstSent || ts < g.firstSent) g.firstSent = ts;
      if (!g.lastSent || ts > g.lastSent) g.lastSent = ts;
    }
  }
  // Convert Sets to counts and sort by most recent
  const groupedSources = Object.values(templateGroups)
    .map(g => ({ ...g, contactCount: g.contacts.size, contacts: undefined }))
    .sort((a, b) => (b.lastSent || "").localeCompare(a.lastSent || ""));

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
    });
  };

  const timeLabel = {
    smart_window: "Smart Window (9am–8pm)",
    honor_prefix: "Honor Time Prefix",
    immediate: "Send Immediately",
  };

  return (
    <div className="p-4 md:p-8" data-testid="fub-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900 flex items-center gap-2">
            <Workflow size={24} style={{ color: "#FF5C35" }} />
            FUB Campaigns
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-0.5">
            Auto-detects [SMS] notes from FUB Automations and sends them as texts
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={syncing}
          className="btn-secondary flex items-center gap-2 px-3 py-2 text-sm rounded-md"
        >
          <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Status Banner */}
      {!loading && (
        <div className={`mb-6 p-4 rounded-lg border ${isEnabled ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {isEnabled ? (
                <ToggleRight size={24} className="text-emerald-600 shrink-0" />
              ) : (
                <ToggleLeft size={24} className="text-amber-600 shrink-0" />
              )}
              <div>
                <p className={`text-sm font-semibold ${isEnabled ? "text-emerald-800" : "text-amber-800"}`}>
                  {isEnabled ? "SMS Detection Active" : "SMS Detection Disabled"}
                </p>
                <p className={`text-xs mt-0.5 ${isEnabled ? "text-emerald-600" : "text-amber-600"}`}>
                  {isEnabled
                    ? `Polling every 2 min · ${timeLabel[timeHandling] || timeHandling}`
                    : "Enable in Settings to start auto-detecting [SMS] notes from FUB"}
                </p>
              </div>
            </div>
            <Link
              to="/settings"
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-colors"
              style={{ backgroundColor: isEnabled ? "#D1FAE5" : "#FEF3C7", color: isEnabled ? "#065F46" : "#92400E" }}
            >
              <Settings size={14} />
              Configure
              <ArrowRight size={12} />
            </Link>
          </div>

          {isEnabled && (!isFubConfigured || !isSmsConfigured) && (
            <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-700 space-y-1">
              {!isFubConfigured && <p className="flex items-center gap-1"><XCircle size={12} /> FUB API key not configured — add it in Settings to connect</p>}
              {!isSmsConfigured && <p className="flex items-center gap-1"><XCircle size={12} /> SignalWire not configured — messages will be logged in mock mode</p>}
            </div>
          )}
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold" style={{ color: "#FF5C35" }}>{sends.length}</div>
            <div className="text-xs text-slate-500 mt-1">Total SMS Sends</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-emerald-600">{stats.delivered}</div>
            <div className="text-xs text-slate-500 mt-1">Delivered</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.pending}</div>
            <div className="text-xs text-slate-500 mt-1">Scheduled</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-red-500">{stats.failed + stats.skipped}</div>
            <div className="text-xs text-slate-500 mt-1">Failed / Skipped</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-lg" style={{ backgroundColor: "#F1F3F5" }}>
        <button
          onClick={() => setTab("overview")}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "overview" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          Campaigns ({groupedSources.length})
        </button>
        <button
          onClick={() => setTab("history")}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "history" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          All Sends ({sends.length})
        </button>
        <button
          onClick={() => { setTab("debug"); if (!debugNotes) loadDebug(); }}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "debug" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          Debug Notes
        </button>
      </div>

      {tab === "overview" && (
        <>
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
            </div>
          ) : groupedSources.length === 0 ? (
            <div className="card p-6 text-center py-12">
              <Workflow size={40} className="mx-auto mb-3 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-600">No Campaign SMS Detected Yet</h3>
              <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
                {isEnabled
                  ? 'Waiting for FUB Automations to create notes with [SMS] prefix. Create an automation in FUB with "Add Note" steps starting with [SMS].'
                  : 'Enable FUB Note SMS Detection in Settings first, then create automations in FUB with "Add Note" steps starting with [SMS].'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedSources.map(group => (
                <div key={group.id} className="card p-0 overflow-hidden" style={{ borderLeft: "3px solid #FF5C35" }}>
                  {/* Campaign header */}
                  <div className="p-4 pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800 text-sm leading-snug">{group.name}</h3>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2 font-mono">{group.template}</p>
                      </div>
                      <button
                        onClick={() => deleteCampaignGroup(group)}
                        disabled={deleting === group.id}
                        className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Delete campaign"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                        <Users size={12} /> {group.contactCount} contact{group.contactCount !== 1 ? "s" : ""}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                        <Send size={12} /> {group.sends.length} send{group.sends.length !== 1 ? "s" : ""}
                      </span>
                      {group.delivered > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                          <CheckCircle size={12} /> {group.delivered}
                        </span>
                      )}
                      {group.mock > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                          Mock: {group.mock}
                        </span>
                      )}
                      {group.scheduled > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-purple-50 text-purple-700">
                          <Clock size={12} /> {group.scheduled} pending
                        </span>
                      )}
                      {group.failed > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-50 text-red-600">
                          <XCircle size={12} /> {group.failed}
                        </span>
                      )}
                      {group.skipped > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-slate-50 text-slate-500">
                          Skipped: {group.skipped}
                        </span>
                      )}
                    </div>
                    {group.lastSent && (
                      <p className="text-[10px] text-slate-400 mt-2">Last sent: {formatDate(group.lastSent)}</p>
                    )}
                  </div>

                  {/* Recipients list */}
                  {group.sends.length > 0 && (
                    <div className="border-t border-slate-100 px-4 py-2.5" style={{ backgroundColor: "#F8F9FB" }}>
                      <div className="space-y-1">
                        {group.sends.slice(0, 5).map((s, i) => (
                          <div key={s.id || i} className="flex items-center gap-2 text-xs py-1">
                            <span className="font-medium text-slate-700 truncate min-w-0 flex-1">{s.contact_name || "Unknown"}</span>
                            <StatusBadge status={s.status} reason={s.reason || s.skip_reason || s.fail_reason} />
                            <span className="text-slate-400 shrink-0 hidden sm:block">{formatDate(s.sent_at || s.created_at)}</span>
                          </div>
                        ))}
                        {group.sends.length > 5 && (
                          <p className="text-[10px] text-slate-400 pt-1">+{group.sends.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "history" && (
        <>
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
            </div>
          ) : sends.length === 0 ? (
            <div className="card p-6 text-center py-12">
              <Send size={40} className="mx-auto mb-3 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-600">No Sends Yet</h3>
              <p className="text-sm text-slate-400 mt-1">
                When FUB automations create notes with [SMS] prefix, sends will appear here.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: "#F8F9FB" }}>
                    <th className="text-left px-4 py-3 font-medium text-slate-500">Contact</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 hidden md:table-cell">Campaign</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 hidden lg:table-cell">Message</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 hidden sm:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sends.map((s, i) => (
                    <tr key={s.id || i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{s.contact_name || "Unknown"}</div>
                        <div className="text-xs text-slate-400">{s.contact_phone}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                        <span className="truncate block max-w-[200px]">{s.source_name || s.action_plan_name || "—"}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                        <span className="truncate block max-w-[250px]">{s.body_resolved || s.body || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={s.status} reason={s.reason || s.skip_reason || s.fail_reason} />
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs hidden sm:table-cell">
                        {formatDate(s.sent_at || s.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "debug" && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">Shows the 30 most recent FUB notes and whether they'd be detected as SMS.</p>
            <button onClick={loadDebug} disabled={loadingDebug} className="btn-secondary text-xs flex items-center gap-1.5">
              <RefreshCw size={13} className={loadingDebug ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
          {debugNotes && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: "#F8F9FB" }}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span>SMS Detection: <strong className={debugNotes.fub_sms_enabled ? "text-green-600" : "text-red-500"}>{debugNotes.fub_sms_enabled ? "Enabled" : "Disabled"}</strong></span>
                  <span>Watermark Note ID: <strong>#{debugNotes.current_watermark}</strong></span>
                </div>
                <button
                  onClick={resetWatermark}
                  disabled={resettingWatermark}
                  className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium disabled:opacity-50"
                >
                  {resettingWatermark ? "Resetting..." : "Reset Watermark"}
                </button>
              </div>
            </div>
          )}
          {loadingDebug ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
            </div>
          ) : debugNotes?.notes?.length === 0 ? (
            <div className="card p-6 text-center py-8">
              <p className="text-slate-500">No recent notes found in FUB</p>
            </div>
          ) : debugNotes?.notes ? (() => {
            // Group notes by date
            const grouped = {};
            debugNotes.notes.forEach(n => {
              const dateStr = n.created ? new Date(n.created).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" }) : "Unknown Date";
              if (!grouped[dateStr]) grouped[dateStr] = [];
              grouped[dateStr].push(n);
            });
            return (
              <div className="space-y-5">
                {Object.entries(grouped).map(([date, notes]) => {
                  const smsCount = notes.filter(n => n.is_sms_detected).length;
                  return (
                    <div key={date}>
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-sm font-semibold text-slate-700">{date}</h4>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">{notes.length} note{notes.length !== 1 ? "s" : ""}</span>
                        {smsCount > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600 font-medium">{smsCount} SMS</span>}
                      </div>
                      <div className="space-y-1.5">
                        {notes.map(n => (
                          <div key={n.note_id} className={`card p-3 ${n.is_sms_detected ? "ring-2 ring-green-200" : ""}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-mono font-bold text-slate-600">#{n.note_id}</span>
                                  <span className="text-xs text-slate-400">Person: {n.person_id}</span>
                                  {n.is_html && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">HTML</span>}
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${n.is_sms_detected ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                                    {n.is_sms_detected ? "SMS DETECTED" : "Not SMS"}
                                  </span>
                                  {n.already_processed && <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded font-medium">Already sent</span>}
                                  {!n.would_be_new && !n.already_processed && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded font-medium">Below watermark</span>}
                                  <span className="text-[10px] text-slate-400">{n.created ? new Date(n.created).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : ""}</span>
                                </div>
                                <div className="mt-1.5 space-y-0.5">
                                  <p className="text-xs text-slate-400 truncate">Cleaned: <span className="font-mono text-slate-700">{n.cleaned_body_preview}</span></p>
                                </div>
                                <div className="text-[10px] text-slate-400 mt-1">
                                  By: {n.created_by || "—"}
                                </div>
                              </div>
                              {n.is_sms_detected && !n.already_processed && (
                                <button
                                  onClick={() => reprocessNote(n.note_id)}
                                  disabled={reprocessing === n.note_id}
                                  className="btn-primary text-xs shrink-0"
                                >
                                  {reprocessing === n.note_id ? "Sending..." : "Reprocess & Send"}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })() : null}
        </>
      )}

      {/* How it works info box */}
      <div className="mt-8 p-4 rounded-lg border" style={{ backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" }}>
        <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <AlertCircle size={16} style={{ color: "#FF5C35" }} />
          How FUB Campaign SMS Works
        </h3>
        <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
          <li>Enable <strong>FUB Note SMS Detection</strong> in <Link to="/settings" className="text-[#FF5C35] underline">Settings</Link></li>
          <li>Create an <strong>Automation</strong> in FollowUp Boss with "Add Note" steps, <strong>or</strong> add a note directly on any contact</li>
          <li>Start each note with <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>[SMS]</code> — this tells Relay to send it as a text</li>
          <li>Use merge fields: <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>%contact_first_name%</code>, <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>%user_first_name%</code></li>
          <li>Optional time prefixes: <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>[9am]</code>, <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>[2:30pm]</code>, <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>[15min]</code>, <code className="px-1 py-0.5 rounded text-xs" style={{ backgroundColor: "#F1F3F5" }}>[1hr]</code></li>
          <li>Relay polls FUB every 2 minutes, detects [SMS] notes, and sends them automatically</li>
        </ol>

        <div className="mt-3 pt-3 border-t border-slate-200">
          <p className="text-xs font-medium text-slate-600 mb-1.5">Example FUB automation note:</p>
          <div className="p-2.5 rounded-md font-mono text-xs" style={{ backgroundColor: "#F1F3F5", color: "#374151" }}>
            [SMS] Hi %contact_first_name%, this is %user_first_name% from the Petrosian Team! I wanted to follow up on your real estate inquiry. Are you still looking?
          </div>
        </div>
      </div>
    </div>
  );
}
